import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useState } from "react";
import { useList } from "../../context/use-list";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash, Eye, MoveUpRight, ExternalLink } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { safeFormatInTimeZone } from "@/utils/format/safe-format-in-time-zone";
import {
  calculateAmountCharged,
  calculateTransferAmount,
  calculateMarginPercentage,
  calculateMarginValue,
  getContractTypeLabel,
  getNextBirthDate,
  getRenewalIndicatorLabel,
  getStatusData,
  getContractAssetsParsed,
} from "../../utils/private-office-contract";
import { formatCurrency } from "@/utils/format/currency";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { generatePrivateRoomUrl } from "@/features/private-room/list/utils/generate-private-room-url";
import { useNavigate } from "react-router-dom";
import {
  PrivateOfficeContract,
  ContractAssetsParsed,
  ParsedContractHistoryEntry,
} from "@/services/private-office-contracts-service/types";
import { useGetPackages } from "@/hooks/use-get-packages/use-get-packages";

export const PrivateOfficeContractsDetails = () => {
  const {
    poContractToDetail,
    setPOContractToDetail,
    fetchDetailsData,
    fetchContractHistory,
  } = useList();
  const { getPackageDetails, packageDetails } = useGetPackages();
  const [contract, setContract] = useState<PrivateOfficeContract | null>(null);
  const [contractHistory, setContractHistory] = useState<
    ParsedContractHistoryEntry[]
  >([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (poContractToDetail) {
      setLoading(true);
      fetchDetailsData(poContractToDetail)
        .then((contractData) => {
          setContract(contractData);
          if (contractData?.packageId && contractData?.company?.id) {
            getPackageDetails(contractData.packageId, contractData.company.id);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setContract(null);
    }
  }, [poContractToDetail]);

  const handleFetchContractHistory = async () => {
    if (!contract) return;

    try {
      setIsLoadingHistory(true);
      const history = await fetchContractHistory(contract.id);
      setContractHistory(history);
    } catch (error) {
      console.error("Failed to fetch contract history:", error);
      setContractHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleCloseModal = () => {
    setPOContractToDetail(null);
  };

  const pricePerCredit = contract?.currentCycle?.credits?.unitPrice || 0;
  const totalAmountCharged = calculateAmountCharged(
    Number(contract?.monthlyCredits),
    pricePerCredit,
  );
  const totalTransferAmount = calculateTransferAmount(
    contract?.takePercentage || "",
    contract?.partnerPrice.toString() || "",
  );
  const totalMarginPercentage = calculateMarginPercentage(
    totalAmountCharged,
    totalTransferAmount,
  );
  const totalMarginValue = calculateMarginValue(
    totalAmountCharged,
    totalTransferAmount,
  );
  const {
    allAssetsByDate,
    firstCompanyAssignmentTerm,
    firstPartnerAssignmentTerm,
  } = getContractAssetsParsed(contract?.assets || []) as ContractAssetsParsed;
  const statusData = getStatusData(contract?.status || "");

  return (
    <Transition.Root show={!!poContractToDetail} as={Fragment}>
      <Dialog
        id="private-office-contracts-details"
        as="div"
        className="relative z-10"
        onClose={handleCloseModal}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto relative w-screen max-w-3xl bg-white flex flex-col">
                  <div className="flex flex-col h-full overflow-y-scroll w-full shadow-xl min-h-full">
                    <div className="sticky top-0 z-10 border-b border-zinc-200 bg-white">
                      <div className="p-6 pb-0">
                        <h2 className="text-xl font-semibold text-zinc-950">
                          Detalhes do contrato:
                        </h2>
                        <div className="text-sm text-gray-500 mb-6">
                          {contract?.space?.name} - {contract?.spot?.label} • ID
                          do espaço: #{contract?.space?.id}
                        </div>
                      </div>
                      <div className="absolute right-0 top-0 flex mr-1 sm:-ml-10 sm:pr-4 p-6">
                        <button
                          type="button"
                          id="close-modal"
                          className="relative bg-white border border-zinc-200 hover:bg-zinc-100 p-2 h-[36px] w-[36px] rounded-md text-zinc-950 flex items-center justify-center"
                          onClick={handleCloseModal}
                        >
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                    <div className="flex-1">
                      <Tabs
                        defaultValue="geral"
                        className="w-full mt-5 p-6 pt-0"
                      >
                        <TabsList className="grid w-full grid-cols-4 mb-6">
                          <TabsTrigger value="geral">Geral</TabsTrigger>
                          <TabsTrigger value="financeiro">
                            Financeiro
                          </TabsTrigger>
                          <TabsTrigger value="detalhes">Detalhes</TabsTrigger>
                          <TabsTrigger value="registros">Registros</TabsTrigger>
                        </TabsList>
                        <TabsContent value="geral">
                          {loading ? (
                            <div className="py-10 text-center text-zinc-400">
                              Carregando...
                            </div>
                          ) : contract ? (
                            <div className="flex flex-col gap-6">
                              <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                                <div className="border-r border-gray-200">
                                  <div className="text-sm text-gray-500 mb-1">
                                    ID do contrato
                                  </div>
                                  <div className="font-semibold">
                                    #{contract?.id}
                                  </div>
                                </div>
                                <div className="border-r border-gray-200">
                                  <div className="text-sm text-gray-500 mb-1">
                                    Créditos
                                  </div>
                                  <div className="font-semibold">
                                    {contract?.monthlyCredits?.toFixed(2)}
                                  </div>
                                </div>
                                <div className="border-r border-gray-200">
                                  <div className="text-sm text-gray-500 mb-1">
                                    Num. Pessoas
                                  </div>
                                  <div className="font-semibold">
                                    {contract?.members}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-500 mb-1">
                                    Status
                                  </div>
                                  <Badge
                                    style={{
                                      backgroundColor: statusData.bgColor,
                                    }}
                                    className="text-gray-800 hover:bg-[${statusData.bgColor}]/80"
                                  >
                                    {statusData.label}
                                  </Badge>
                                </div>
                              </div>

                              <div className="pl-4">
                                <div className="text-sm text-gray-500 mb-1">
                                  Empresa:
                                </div>
                                <div className="font-medium">
                                  {contract?.company?.name}
                                </div>
                              </div>
                              <div className="pl-4">
                                <div className="text-sm text-gray-500 mb-1">
                                  Espaço:
                                </div>
                                <div className="font-medium">
                                  {contract?.space?.name}
                                </div>
                              </div>
                              <div className="pl-4">
                                <div className="text-sm text-gray-500 mb-1">
                                  Escritório:
                                </div>
                                <div className="font-medium">
                                  {contract?.spot?.label}
                                </div>
                              </div>
                              <div className="pl-4">
                                <div className="text-sm text-gray-500 mb-1">
                                  Tipo de reserva
                                </div>
                                <div className="font-medium">
                                  {getContractTypeLabel(contract?.type)}
                                </div>
                              </div>

                              <div className="grid grid-cols-3 gap-4 pl-4">
                                <div>
                                  <div className="text-sm text-gray-500 mb-1">
                                    Data de início :
                                  </div>
                                  <div className="font-medium">
                                    {safeFormatInTimeZone(
                                      contract.startDate,
                                      "UTC",
                                      "dd/MM/yyyy",
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-500 mb-1">
                                    Data de término:
                                  </div>
                                  <div className="font-medium">
                                    {safeFormatInTimeZone(
                                      contract?.endDate,
                                      "UTC",
                                      "dd/MM/yyyy",
                                      "Indeterminado",
                                    )}
                                  </div>
                                </div>
                                {contract?.cancelledAt && (
                                  <div>
                                    <div className="text-sm text-gray-500 mb-1">
                                      Data de cancelamento:
                                    </div>
                                    <div className="font-medium">
                                      {safeFormatInTimeZone(
                                        contract?.cancelledAt,
                                        "UTC",
                                        "dd/MM/yyyy",
                                        "-",
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {contract?.cancelledAt && (
                                <div className="pl-4">
                                  <div className="text-sm text-gray-500 mb-1">
                                    Motivo do cancelamento
                                  </div>
                                  <div className="font-medium">
                                    {contract?.cancellationReason}
                                  </div>
                                </div>
                              )}

                              <div className="grid grid-cols-2 gap-4 pl-4">
                                <div>
                                  <div className="text-sm text-gray-500 mb-1">
                                    Pessoa responsável pelo contrato (cliente)
                                  </div>
                                  <div className="font-medium">
                                    {contract?.user?.name}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-500 mb-1">
                                    E-mail (responsável pelo contrato)
                                  </div>
                                  <div className="font-medium">
                                    {contract?.user?.email}
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-4 pl-4">
                                <div>
                                  <div className="text-sm text-gray-500 mb-1">
                                    Pessoa responsável pela gestão das salas
                                    <br /> (cliente)
                                  </div>
                                  <div className="font-medium">
                                    {contract?.managerUser?.name}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-500 mb-1">
                                    E-mail (responsável pela gestão)
                                  </div>
                                  <div className="font-medium">
                                    {contract?.managerUser?.email}
                                  </div>
                                </div>
                              </div>
                              <div className="pl-4">
                                <h3 className="font-semibold text-gray-900 mb-4">
                                  Observações
                                </h3>
                                <div className="text-sm font-medium text-gray-900 mb-2">
                                  Notas
                                </div>
                                <div className="flex flex-col gap-2 border border-gray-200 rounded-lg p-4">
                                  <div className="text-sm text-gray-600 mb-4">
                                    {contract?.notes}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="py-10 text-center text-zinc-400">
                              Nenhum dado encontrado.
                            </div>
                          )}
                        </TabsContent>

                        <TabsContent value="financeiro">
                          <div className="flex flex-col gap-6 min-h-full">
                            <div className="pl-4">
                              <div className="text-sm text-gray-500 mb-1">
                                Créditos mensais:
                              </div>
                              <div className="font-medium">
                                {contract?.monthlyCredits?.toFixed(2)}
                              </div>
                            </div>
                            <div className="pl-4">
                              <div className="text-sm text-gray-500 mb-1">
                                Valor cobrado:
                              </div>
                              <div className="font-medium">
                                {totalAmountCharged
                                  ? formatCurrency(totalAmountCharged)
                                  : "-"}
                              </div>
                            </div>
                            <div className="pl-4">
                              <div className="text-sm text-gray-500 mb-1">
                                Valor do parceiro:
                              </div>
                              <div className="font-medium">
                                {formatCurrency(Number(contract?.partnerPrice))}
                              </div>
                            </div>
                            <div className="pl-4">
                              <div className="text-sm text-gray-500 mb-1">
                                Take:
                              </div>
                              <div className="font-medium">
                                {contract?.takePercentage}%
                              </div>
                            </div>
                            <div className="pl-4">
                              <div className="text-sm text-gray-500 mb-1">
                                Repasse:
                              </div>
                              <div className="font-medium">
                                {totalTransferAmount
                                  ? formatCurrency(totalTransferAmount)
                                  : "-"}
                              </div>
                            </div>
                            <div className="pl-4">
                              <div className="text-sm text-gray-500 mb-1">
                                Margem:
                              </div>
                              <div className="font-medium">
                                {totalMarginPercentage
                                  ? `${totalMarginPercentage}%`
                                  : "-"}
                              </div>
                            </div>
                            <div className="pl-4">
                              <div className="text-sm text-gray-500 mb-1">
                                Margem absoluta:
                              </div>
                              <div className="font-medium">
                                {totalMarginValue
                                  ? formatCurrency(totalMarginValue)
                                  : "-"}
                              </div>
                            </div>
                          </div>
                        </TabsContent>

                        <TabsContent value="detalhes">
                          <div className="grid grid-cols-2 gap-8 p-4 pt-0">
                            <div>
                              <h3 className="font-semibold text-gray-900 mb-4">
                                Detalhes adicionais
                              </h3>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  ID do pacote:
                                </span>
                                <br />
                                <span className="font-medium">
                                  #{contract?.packageId}
                                </span>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  ID do contrato de créditos:
                                </span>
                                <br />
                                <a
                                  href={`${import.meta.env.VITE_OLD_BACKOFFICE_URL}#/package/list/${contract?.companyId}/${packageDetails?.contractId}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex items-center gap-1"
                                >
                                  <span className="font-medium underline cursor-pointer">
                                    {packageDetails?.contractId
                                      ? `#${packageDetails.contractId}`
                                      : "-"}
                                  </span>
                                  <ExternalLink className="w-4 h-4 cursor-pointer" />
                                </a>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  Data aniversário:
                                </span>
                                <br />
                                <span className="font-medium">
                                  {contract?.startDate
                                    ? getNextBirthDate(contract?.startDate)
                                    : "-"}
                                </span>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  Tempo de uso do escritório:
                                </span>
                                <br />
                                <span className="font-medium">
                                  {contract?.startDate
                                    ? formatDistanceToNow(
                                        new Date(contract.startDate),
                                        {
                                          locale: ptBR,
                                        },
                                      )
                                    : "-"}
                                </span>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  Vigência prevista:
                                </span>
                                <br />
                                <span className="font-medium">
                                  {contract?.expectedDurationMonths
                                    ? `${contract?.expectedDurationMonths} meses`
                                    : "-"}
                                </span>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  Aviso prévio cliente:
                                </span>
                                <br />
                                <span className="font-medium">
                                  {contract?.noticeInDaysCompany
                                    ? `${contract?.noticeInDaysCompany} dias`
                                    : "-"}
                                </span>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  Aviso prévio parceiro:
                                </span>
                                <br />
                                <span className="font-medium">
                                  {contract?.noticeInDaysPartner
                                    ? `${contract?.noticeInDaysPartner} dias`
                                    : "-"}
                                </span>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  Índice de reajuste
                                </span>
                                <br />
                                <span className="font-medium">
                                  {getRenewalIndicatorLabel(
                                    contract?.renewalIndicator || "",
                                  )}
                                </span>
                              </div>
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900 mb-4">
                                Espaço
                              </h3>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  ID do espaço:
                                </span>
                                <br />
                                <span className="font-medium">
                                  #{contract?.space?.id}
                                </span>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  Espaço
                                </span>
                                <br />
                                <a
                                  href={`${import.meta.env.VITE_PORTAL_URL}/coworking/space/${contract?.space?.vanishName}`}
                                  target="_blank"
                                  className="flex items-center gap-1"
                                  rel="noreferrer"
                                >
                                  <span className="font-medium underline cursor-pointer">
                                    {contract?.space?.name} - #
                                    {contract?.space?.id}
                                  </span>
                                  <ExternalLink className="w-4 h-4 cursor-pointer" />
                                </a>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  ID do escritório:
                                </span>
                                <br />
                                <span className="font-medium">
                                  #{contract?.spotId}
                                </span>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  Escritório:
                                </span>
                                <br />
                                <a
                                  href={generatePrivateRoomUrl(
                                    contract?.spot?.id?.toString() || "",
                                    contract?.currentCycle?.credits?.unitPrice?.toString() ||
                                      "",
                                  )}
                                  target="_blank"
                                  className="flex items-center gap-1"
                                  rel="noreferrer"
                                >
                                  <span className="font-medium underline cursor-pointer">
                                    {contract?.spot?.label}
                                  </span>
                                  <ExternalLink className="w-4 h-4 cursor-pointer" />
                                </a>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  Endereço:
                                </span>
                                <br />
                                <span className="font-medium">
                                  {contract?.space?.address || "-"}
                                </span>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  CM Responsável:
                                </span>
                                <br />
                                <span className="font-medium">
                                  {contract?.space?.cmName || "-"}
                                </span>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  CM E-mail:
                                </span>
                                <br />
                                <span className="font-medium">
                                  {contract?.space?.cmEmail || "-"}
                                </span>
                              </div>
                              <div className="mb-4">
                                <span className="text-sm text-gray-500">
                                  CM Telefone:
                                </span>
                                <br />
                                <span className="font-medium">
                                  {contract?.space?.cmPhone || "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </TabsContent>

                        <TabsContent value="registros">
                          <div className="flex flex-col gap-6 pt-0">
                            {firstCompanyAssignmentTerm &&
                              firstPartnerAssignmentTerm && (
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                  <h3 className="font-semibold text-gray-900 mb-4">
                                    Documentos atuais
                                  </h3>
                                  <div className="mb-4">
                                    <div className="text-sm text-gray-700 mb-1">
                                      Termo de aceite
                                    </div>
                                    <div className="flex gap-2 items-center">
                                      <input
                                        className="border rounded px-2 py-1 text-sm w-64 focus:outline-none w-full truncate"
                                        value={
                                          firstCompanyAssignmentTerm?.fileName ||
                                          ""
                                        }
                                        readOnly
                                      />
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex gap-1 items-center"
                                        onClick={() =>
                                          window.open(
                                            firstCompanyAssignmentTerm?.url ||
                                              "",
                                            "_blank",
                                          )
                                        }
                                      >
                                        <Eye className="w-4 h-4" /> Visualizar
                                      </Button>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-sm text-gray-700 mb-1">
                                      Termo de ciência
                                    </div>
                                    <div className="flex gap-2 items-center">
                                      <input
                                        className="border rounded px-2 py-1 text-sm w-64 focus:outline-none w-full truncate"
                                        value={
                                          firstPartnerAssignmentTerm?.fileName ||
                                          ""
                                        }
                                        readOnly
                                      />
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex gap-1 items-center"
                                        onClick={() =>
                                          window.open(
                                            firstPartnerAssignmentTerm?.url ||
                                              "",
                                            "_blank",
                                          )
                                        }
                                      >
                                        <Eye className="w-4 h-4" /> Visualizar
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}

                            <Accordion
                              type="single"
                              collapsible
                              className="bg-white rounded-lg p-4 border border-gray-200 py-0"
                            >
                              <AccordionItem
                                value="main-item"
                                className="border-b-0"
                              >
                                <AccordionTrigger>
                                  <h3 className="font-semibold text-gray-900">
                                    Histórico de documentos enviados
                                  </h3>
                                </AccordionTrigger>
                                <AccordionContent>
                                  <Accordion
                                    type="single"
                                    collapsible
                                    defaultValue="item-1"
                                  >
                                    {allAssetsByDate &&
                                      allAssetsByDate.map(
                                        (assetByDate, index: number) => (
                                          <AccordionItem
                                            key={index}
                                            value={`item-${index + 1}`}
                                            className="border-t"
                                          >
                                            <AccordionTrigger>
                                              <div className="font-medium text-sm mb-2">
                                                {assetByDate.createdAt}
                                              </div>
                                            </AccordionTrigger>
                                            {assetByDate.assets.map((asset) => (
                                              <AccordionContent key={asset.id}>
                                                <div className="mb-2">
                                                  <div className="text-sm text-gray-700 mb-1">
                                                    {asset.type ===
                                                    "company_assignment_term"
                                                      ? "Termo de aceite"
                                                      : "Termo de ciência"}
                                                  </div>
                                                  <div className="flex gap-2 items-center">
                                                    <input
                                                      className="border rounded px-2 py-1 text-sm w-64 focus:outline-none w-full truncate"
                                                      value={
                                                        asset?.fileName || ""
                                                      }
                                                      readOnly
                                                    />
                                                    <Button
                                                      variant="outline"
                                                      size="sm"
                                                      className="flex gap-1 items-center"
                                                      onClick={() =>
                                                        window.open(
                                                          asset?.url || "",
                                                          "_blank",
                                                        )
                                                      }
                                                    >
                                                      <Eye className="w-4 h-4" />{" "}
                                                      Visualizar
                                                    </Button>
                                                  </div>
                                                </div>
                                              </AccordionContent>
                                            ))}
                                          </AccordionItem>
                                        ),
                                      )}
                                  </Accordion>
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                            <Accordion
                              type="single"
                              collapsible
                              className="bg-white rounded-lg p-4 border border-gray-200 py-0"
                              onValueChange={(value) => {
                                if (value === "main-item" && contract) {
                                  handleFetchContractHistory();
                                }
                              }}
                            >
                              <AccordionItem
                                value="main-item"
                                className="border-b-0"
                              >
                                <AccordionTrigger>
                                  <h3 className="font-semibold text-gray-900">
                                    Histórico de edições
                                  </h3>
                                </AccordionTrigger>
                                <AccordionContent>
                                  <AccordionItem
                                    value="item-1"
                                    className="border-t py-4"
                                  >
                                    <div className="flex flex-col gap-4 pl-2">
                                      {isLoadingHistory ? (
                                        <div className="flex flex-col gap-4 pl-2">
                                          <div className="border-l-2 border-gray-200 pt-1">
                                            <div className="flex items-center gap-2">
                                              <Skeleton className="w-4 h-4 rounded-full -ml-[9px]" />
                                              <Skeleton className="h-5 w-32" />
                                            </div>
                                            <div className="ml-4 mt-2">
                                              <Skeleton className="h-4 w-48" />
                                            </div>
                                            <div className="ml-4 mt-2">
                                              <div className="ml-8">
                                                <Skeleton className="h-3 w-40 mb-1" />
                                                <Skeleton className="h-3 w-36" />
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ) : contractHistory.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500">
                                          <p>
                                            Este contrato não sofreu alterações
                                          </p>
                                        </div>
                                      ) : (
                                        contractHistory.map(
                                          (history, index) => (
                                            <div
                                              key={index}
                                              className="flex flex-col gap-4 pl-2"
                                            >
                                              <div className="border-l-2 border-gray-200 pt-1">
                                                <div className="flex items-center gap-2">
                                                  <span
                                                    className={`w-4 h-4 ${history.iconColor} rounded-full inline-block -ml-[9px] relative`}
                                                  ></span>
                                                  <span className="font-semibold">
                                                    {history.title}
                                                  </span>
                                                </div>
                                                <div className="text-sm ml-4">
                                                  {history.subtitle}
                                                </div>
                                                {history.alteredFields && (
                                                  <div className="text-sm ml-4">
                                                    <ul className="text-sm text-gray-500 ml-8 list-disc mt-1 pl-2">
                                                      {history.alteredFields.map(
                                                        (field, fieldIndex) => (
                                                          <li key={fieldIndex}>
                                                            {field}
                                                          </li>
                                                        ),
                                                      )}
                                                    </ul>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          ),
                                        )
                                      )}
                                    </div>
                                  </AccordionItem>
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          </div>
                        </TabsContent>
                      </Tabs>
                    </div>
                    <div className="sticky bottom-0 flex justify-end gap-4 p-6 border-t border-gray-200 bg-white w-full relative">
                      {!contract?.cancelledAt && (
                        <>
                          <Button
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() =>
                              navigate(
                                `/private-office/contracts/cancel/${contract?.id}`,
                              )
                            }
                          >
                            <Trash className="w-4 h-4 mr-2" />
                            Cancelar
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() =>
                              navigate(
                                `/private-office/contracts/update/${contract?.id}`,
                              )
                            }
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </Button>
                        </>
                      )}
                      <Button
                        className="bg-gray-900 hover:bg-gray-800 text-white"
                        onClick={() =>
                          navigate(
                            `/private-office/bookings?page=1&po_contract_id=${contract?.id}`,
                          )
                        }
                      >
                        <MoveUpRight className="w-4 h-4 mr-2" />
                        Ver bookings
                      </Button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
